import { SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParams } from 'use-query-params';

import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useFetchRecentTransactionsByChargerIdQuery } from '../../services/history.api';
import { useFetchSessionsByChargerIdQuery } from '../../services/session.api';
import { Location, Session } from '../../stores/types';
import { convertDateToString, convertMilliSecondToDate, getLastMonth } from '../../utils/Date.Util';
import { Card, CustomDatePicker, MODAL_TYPES, useGlobalModalContext } from '../_ui';
import { DATE_RANGE } from '../_ui/date-picker/Custom-Date-Picker.component';
import { SessionDetail } from './SessionDetail.component';
import { SessionDetailData } from './SessionDetail.interface';
import { SessionTable } from './SessionTable.component';
import {
  generateChargerStatusHistory,
  generateFullAddress,
  getChargerStatus,
  getSortedRecentSessions,
} from './utils';

interface ChargerSessionRowPort {
  id: string;
  physicalLocation: string;
  connectorType: string;
  charger: {
    id: string;
    name: string;
    type: string;
    model: string;
    location: Location;
  };
}

/** Session or transaction row from the charger sessions table. */
interface ChargerSessionRowData {
  id: string;
  port: ChargerSessionRowPort;
  startedVia: string;
  userEmail: string;
  pluggedInTimeSeconds?: number;
  effectiveChargingTimeSeconds?: number;
  consumedEnergyKwh?: number;
  startTime?: Date;
  stopTime?: Date;
  billedTotalAmount?: number;
  billedCurrency?: string;
  rfids?: string[];
  vehicleIdActive?: boolean;
  receiptId?: string;
  paymentTerminalType?: string;
  createTime?: Date;
  completeTime?: Date;
  status?: string;
  accumulatedTotals?: {
    pluggedInTimeSeconds: number;
    effectiveChargingTimeSeconds: number;
    consumedEnergyKwh: number;
    cost: number;
    currency: string;
  };
  transactionId?: string;
  vehicleName?: string;
  paymentTerminalSerialNumber?: string;
  paymentTerminalSessionId?: string;
  rawChargerStatus?: Session['rawChargerStatus'];
}

interface ChargerSessionsProps {
  chargerId: string;
  companyId: string;
  currentPage?: number;
  setCurrentPage: (page: number) => void;
  isLocalTimezone: boolean;
  setIsLocalTimezone: (selected: boolean) => void;
}

export const ChargerSessions = ({
  chargerId,
  companyId,
  currentPage: controlledCurrentPage,
  setCurrentPage: controlledSetCurrentPage,
  isLocalTimezone: controlledIsLocalTimezone,
  setIsLocalTimezone: controlledSetIsLocalTimezone,
}: ChargerSessionsProps) => {
  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();
  const { LAST_WEEK, LAST_MONTH, CUSTOM_RANGE } = DATE_RANGE;

  const [sessionQuery, setSessionQuery] = useQueryParams({
    start: StringParam,
    end: StringParam,
    option: StringParam,
  });

  const dateRange = useMemo(() => {
    const lastMonth = getLastMonth();
    return [
      sessionQuery.start ? convertMilliSecondToDate(sessionQuery.start) : lastMonth[0],
      sessionQuery.end ? convertMilliSecondToDate(sessionQuery.end) : lastMonth[1],
    ];
  }, [sessionQuery]);

  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalIsLocalTimezone, setInternalIsLocalTimezone] = useState(true);

  const currentPage = controlledCurrentPage ?? internalCurrentPage;
  const setCurrentPage = controlledSetCurrentPage ?? setInternalCurrentPage;
  const isLocalTimezone = controlledIsLocalTimezone ?? internalIsLocalTimezone;
  const setIsLocalTimezone = controlledSetIsLocalTimezone ?? setInternalIsLocalTimezone;

  const sessionQueryArgs = useMemo(
    () => ({
      chargerId,
      sort: 'desc' as const,
      dateRange,
      filter_hasCompletedTx: false,
    }),
    [chargerId, dateRange],
  );
  const { data: sessionData, isLoading: isSessionLoading } = useFetchSessionsByChargerIdQuery(
    sessionQueryArgs,
    { skip: !chargerId || !companyId },
  );

  const { transactions, isTransactionLoading } = useFetchRecentTransactionsByChargerIdQuery(
    { chargerId, companyId, sort: 'desc', dateRange },
    {
      skip: !chargerId || !companyId,
      selectFromResult: (endPoint) => ({
        transactions: endPoint.data ? endPoint.data.entities : [],
        isTransactionLoading: endPoint.isFetching,
      }),
    },
  );

  const isLoading = isSessionLoading || isTransactionLoading;

  const [sorting, setSorting] = useLocalStorageState<SortingState>(
    `chargerSessions-sortConfig`,
    [],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, setCurrentPage]);

  const recentSessions = useMemo(() => {
    return getSortedRecentSessions(sessionData?.entities ?? [], transactions, [], dateRange);
  }, [sessionData?.entities, transactions, dateRange]);

  const renderSessionDetail = useCallback(
    (rowData: ChargerSessionRowData, islocal: boolean) => {
      const location = rowData.port.charger.location;
      const SessionDetailInfo: SessionDetailData = {
        id: rowData.id,
        startTime: rowData.startTime ?? rowData.createTime,
        endTime: rowData.stopTime ?? rowData.completeTime,
        timeZone: islocal
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : rowData.port.charger.location.timeZone,
        authenticationType: rowData.startedVia,
        vehicleName: rowData.vehicleName ?? '',
        isVehicleActive: rowData.vehicleIdActive,
        rfids: rowData.rfids ?? [],
        chargerId: rowData.port.charger.id,
        charger: rowData.port.charger.name,
        connector: rowData.port.connectorType,
        connectorSide: t(rowData.port.physicalLocation.toLowerCase()),
        location: rowData.port.charger.location.name ?? '',
        address: generateFullAddress(location),
        kwhUsed: rowData.consumedEnergyKwh ?? rowData.accumulatedTotals?.consumedEnergyKwh,
        cost: rowData.billedTotalAmount ?? rowData.accumulatedTotals?.cost,
        currency: rowData.billedCurrency ?? rowData.accumulatedTotals?.currency,
        sessionStatus: rowData.status,
        userEmail: rowData.userEmail,
        statusHistory: generateChargerStatusHistory(
          getChargerStatus(rowData),
          rowData.startTime ?? rowData.createTime,
          rowData.stopTime ?? rowData.completeTime,
          t,
          islocal
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : rowData.port.charger.location.timeZone,
        ),
        receiptId: rowData.receiptId,
        paymentTerminalSerialNumber: rowData.paymentTerminalSerialNumber,
        paymentTerminalSessionId: rowData.paymentTerminalSessionId,
      };
      return <SessionDetail sessionData={SessionDetailInfo} />;
    },
    [t],
  );

  const rowClick = useCallback(
    (rowData: ChargerSessionRowData) => {
      showModal(MODAL_TYPES.INFO_MODAL, {
        title: t('session_detail'),
        height: 'max-content',
        onRenderBody: () => renderSessionDetail(rowData, isLocalTimezone),
      });
    },
    [isLocalTimezone, renderSessionDetail, showModal, t],
  );

  return (
    <Card className={'pt-0 pl-0 pb-0 pr-0'}>
      <div className='flex mt-3 w-full'>
        <div className='flex w-4/5'>
          <CustomDatePicker
            optionList={[LAST_WEEK, LAST_MONTH, CUSTOM_RANGE]}
            format='MMM d,yyyy'
            onChange={(option, range: [Date, Date]) => {
              setSessionQuery({
                option,
                start: convertDateToString(range[0]),
                end: convertDateToString(range[1]),
              });
            }}
            selectedOption={sessionQuery.option || DATE_RANGE.LAST_MONTH}
            selectedDate={
              sessionQuery.start && sessionQuery.end
                ? [
                    convertMilliSecondToDate(sessionQuery.start),
                    convertMilliSecondToDate(sessionQuery.end),
                  ]
                : []
            }
            showHighligtHeader={false}
            dataTestId='session-select-date-range'
          />
        </div>
      </div>
      <SessionTable
        data={recentSessions}
        isLoading={isLoading}
        currentPage={currentPage}
        onLoadPage={(page) => setCurrentPage(page)}
        sorting={sorting}
        setSorting={setSorting}
        dataMap={['startTime', 'status', 'port', 'consumedEnergyKwh', 'billedTotalAmount']}
        onRowClick={rowClick}
        isLocalTimezone={isLocalTimezone}
        onToggleTimezone={setIsLocalTimezone}
        t={t}
      />
    </Card>
  );
};
